import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import Security from '@material-ui/icons/Security';
import { ModalActionsComponent, PasslyValidatedPassword, PasslyTextField } from '@app/components';
import { getModalSwitch } from '@app/store/actions';
import { myProfileService } from '@app/services';
import { TranslationsTypes } from '@app/models';
import { onPressEnterModal } from '@app/helpers';

const initialState = {
    verifyPassword: '',
    validatedPassword: '',
    confirmPassword: '',
    errorMessage: '',
    validatedPasswordError: false,
    confirmPasswordError: false
};

interface IReceivedData {
    passwordPolicy: any;
    handleNewPassword: Function;
}

export default function ChangePasswordDialog(props: IReceivedData) {
    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    enum steps {
        STEP1,
        STEP2,
        STEP3
    }

    const [stepOn, setStepOn] = useState(steps.STEP1);
    const [primaryBtnText, setPrimaryBtnText] = useState<TranslationsTypes>('VALIDATE_PHONE');
    const [primaryText, setPrimaryText] = useState<TranslationsTypes>('CURRENT_PASSWORD_PROOF');
    const [disablePrimaryBtn, setDisablePrimaryBtn] = useState(true);
    const [validatedPasswordError, setValidatedPasswordError] = useState(false);
    const [{ verifyPassword, validatedPassword, confirmPassword, errorMessage, confirmPasswordError }, setFormState] = useState(initialState);

    useEffect(() => {
        if (!validatedPasswordError && !confirmPasswordError && confirmPassword !== '' && validatedPassword !== '') {
            setDisablePrimaryBtn(false);
        } else {
            setDisablePrimaryBtn(true);
        }
    }, [validatedPasswordError, confirmPasswordError, confirmPassword, validatedPassword]);

    useEffect(() => {
        if (confirmPassword !== '' && confirmPassword !== validatedPassword) {
            setFormState((previousState) => ({ ...previousState, confirmPasswordError: true }));
        } else {
            setFormState((previousState) => ({ ...previousState, confirmPasswordError: false }));
        }
    }, [confirmPassword, validatedPassword]);

    const handleSubmitBtn = () => {
        let postObj: any = {};

        if (stepOn === steps.STEP1) {
            postObj = {
                currentPassword: verifyPassword
            };

            myProfileService.checkPassword(postObj, handleSuccessVerify);
        } else if (stepOn === steps.STEP2) {
            postObj = {
                currentPassword: verifyPassword,
                isSetPassword: false,
                newPassword: confirmPassword
            };

            myProfileService.putPassword(postObj, handleSuccessChangePassword, handleErrorChangePassword);
        } else if (stepOn === steps.STEP3) {
            props.handleNewPassword();
            setFormState(initialState);
            dispatch(getModalSwitch(false));
        }
    };

    const handleSuccessVerify = (response: any) => {
        if (response.data.status === 200) {
            setStepOn(steps.STEP2);
            setPrimaryBtnText('CHANGE_PASSWORD');
            setPrimaryText('SET_PASSWORD_INSTRUCTIONS');
            setDisablePrimaryBtn(true);
            setFormState((previousState) => ({ ...previousState, errorMessage: '' }));
        } else {
            setFormState((previousState) => ({ ...previousState, errorMessage: translation('PASSWORD_FAILED') }));
        }
    };

    const handleCancel = () => {
        dispatch(getModalSwitch(false));
    };

    const handleSuccessChangePassword = () => {
        setStepOn(steps.STEP3);
        setPrimaryBtnText('MOBILE_PHONE_CHANGE_SUCCESS_BUTTON');
        setPrimaryText('PASSWORD_HAS_CHANGED');
        setFormState((previousState) => ({ ...previousState, errorMessage: '' }));
    };

    const handleErrorChangePassword = (error: any) => {
        let message: string;
        if (error && error.response && error.response.status === 400) {
            message = error.response.data.message;
        } else {
            message = error.response.data.reason;
        }

        setFormState((previousState) => ({ ...previousState, errorMessage: message }));
    };

    const onVerifyPasswordChange = (e: any) => {
        const { name, value } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: value }));

        if (value !== '') {
            setDisablePrimaryBtn(false);
        }
    };

    const onConfirmPasswordChange = (e: any) => {
        const { name, value } = e.target;
        setFormState((previousState) => ({ ...previousState, [name]: value }));

        if (value !== validatedPassword) {
            return false;
        }
    };

    const onValidatedPasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        let value = event.target.value;
        setFormState((previousState) => ({ ...previousState, validatedPassword: value }));
    };

    return (
        <>
            <div className='modal__form' tabIndex={0} onKeyPress={onPressEnterModal(handleSubmitBtn, disablePrimaryBtn)}>
                <div className='modal__medium flex1'>
                    <div>
                        {stepOn !== steps.STEP3 && (
                            <div className='modal__body--row'>
                                <div className='modal__dialog--icon'>
                                    <Security />
                                </div>
                                <div>{translation(primaryText)}</div>
                            </div>
                        )}

                        {stepOn === steps.STEP1 && (
                            <div className='modal__body--row'>
                                <PasslyTextField
                                    fullWidth
                                    name='verifyPassword'
                                    type='password'
                                    label={'PASSWORD'}
                                    value={verifyPassword}
                                    onChange={onVerifyPasswordChange}
                                    id='verifyPassword-input'
                                    required
                                    autoFocus
                                />
                            </div>
                        )}

                        {stepOn === steps.STEP2 && (
                            <>
                                <form className='modal__form' method='post'>
                                    <div className='modal__form-row'>
                                        <PasslyValidatedPassword
                                            value={validatedPassword}
                                            previousPassword={verifyPassword}
                                            onChange={onValidatedPasswordChange}
                                        />
                                    </div>
                                    <div className='modal__form-row'>
                                        <PasslyTextField
                                            fullWidth
                                            name='confirmPassword'
                                            type='password'
                                            label={'CONFIRM_PASSWORD'}
                                            value={confirmPassword}
                                            onChange={onConfirmPasswordChange}
                                            id='confirmPassword-input'
                                            error={confirmPasswordError}
                                            helperText={confirmPasswordError ? translation('PASSWORDS_DONT_MATCH') : ''}
                                            required
                                        />
                                    </div>
                                </form>
                            </>
                        )}
                        {errorMessage && <div className='modal__error'>{errorMessage}</div>}
                    </div>

                    {stepOn === steps.STEP3 && (
                        <>
                            <div className='modal__dialog--success'>
                                <VerifiedUser />
                                <div className='modal__body--centered_content'>{translation(primaryText)}</div>
                            </div>
                        </>
                    )}
                </div>
            </div>
            <ModalActionsComponent
                primaryBtn={translation(primaryBtnText)}
                disabled={disablePrimaryBtn}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleSubmitBtn}
                onClickSecondary={handleCancel}
                id='changePassword'
            />
        </>
    );
}
